import {
  Component,
  EventEmitter,
  OnDestroy,
  Output,
  Signal,
  effect,
  inject,
} from "@angular/core";
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { StateModule } from "projects/shared-state/lib/state.module";
import { StateService } from "projects/shared-state/lib/state.service";
import {
  FilePromptConfig,
  Workflow,
} from "projects/shared-state/lib/types/types";
import { WorkflowState } from "projects/shared-state/public-api";
import { Subscription, tap } from "rxjs";
import { CommonModule } from "@angular/common";
import { FileDropDirective } from "projects/shared-new/src/lib/directives/file-drop.directive";
import { MatIconModule } from "@angular/material/icon";
import { ActivatedRoute } from "@angular/router";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { AiHubLoaderService } from "@aihub/ui/loader";
import { toSignal } from "@angular/core/rxjs-interop";
import { MatExpansionModule } from "@angular/material/expansion";

@Component({
  standalone: true,
  selector: "post-description",
  templateUrl: "./post-description.component.html",
  styleUrls: ["./post-description.component.scss"],
  imports: [
    StateModule,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    FileDropDirective,
    MatIconModule,
    MatProgressSpinnerModule,
    MatExpansionModule,
  ],
})
export class PostDescriptionComponent implements OnDestroy {
  @Output() disablePageEvent = new EventEmitter<string>();

  stateService = inject(StateService);
  activeRouter = inject(ActivatedRoute);
  loaderService = inject(AiHubLoaderService);

  workflowForm: FormGroup = new FormGroup({
    postDescription: new FormControl("", [Validators.required]),
  });

  panelOpenState = false;
  charCount: Subscription;
  postDescriptionCount: number = 0;
  postDescriptionMax: number = 500;
  isLoading$ = toSignal(this.loaderService.loader$);

  stateSignal$: Signal<{
    workflow: WorkflowState;
  }> = this.stateService.getAppState();

  constructor() {
    effect(() => {
      this.charCount = this.workflowForm.valueChanges
        .pipe(
          tap((workflowForm) => {
            this.postDescriptionCount = workflowForm.postDescription?.length;
          }),
        )
        .subscribe();

      const workflow: Workflow = this.stateSignal$().workflow.workflow;
      if (workflow) {
        this.workflowForm.patchValue({
          postDescription:
            workflow.promptWorkflow?.userInputs?.postDescription || "",
        });
        if (workflow.promptWorkflow.aiResults.fileSummary?.length > 0) {
          this.panelOpenState = true;
        }
      }
    });
  }

  askGenAi(): void {
    var promptConfiguration: FilePromptConfig = {
      promptIdentifier: "post_description",
      replacementDict: [
        {
          replacement_key: "post_description",
          replacement_value: this.workflowForm.value.postDescription,
          replacement_type: "plain",
        },
        {
          replacement_key: "profile_summary",
          replacement_value:
            this.stateSignal$().workflow.profile.appProfile.profileSummary ||
            "",
          replacement_type: "plain",
        },
      ],
    };
    if (
      this.stateSignal$().workflow.workflow.promptWorkflow.aiResults.fileSummary?.length > 0
    ) {
      promptConfiguration.replacementDict = [
        ...promptConfiguration.replacementDict,
        ...[
          {
            replacement_key: "fileContent",
            replacement_value:
              this.stateSignal$().workflow.workflow.promptWorkflow.aiResults
                .fileSummary,
            replacement_type: "plain",
          },
        ],
      ];
    } else if (
      this.stateSignal$().workflow.workflow.promptWorkflow.uploadedFiles?.length > 0
    ) {
      promptConfiguration.replacementDict = [
        ...promptConfiguration.replacementDict,
        ...[
          {
            replacement_key: "fileContent",
            replacement_value:
              this.stateSignal$().workflow.workflow.promptWorkflow
                .uploadedFiles[0].result,
            replacement_type: "file",
          },
        ],
      ];
    }
    this.saveWorkflow();
    this.stateService.askGenAiApi(
      promptConfiguration,
      "postDescription",
      "postProcess",
    );
    this.disablePageEvent.emit("postDescription");
  }

  patchPostDescription(value: string): void {
    this.workflowForm.patchValue({
      postDescription: value || "",
    });
    this.updateWorkflow();
  }

  saveWorkflow(): void {
    this.stateService.saveWorkflow(
      {
        ...this.stateSignal$().workflow.workflow,
        name: this.stateSignal$().workflow.workflow.name || "new post",
        status: "draft",
        step: 1,
        activeStep: 2,
        promptWorkflow: {
          userInputs: {
            ...this.stateSignal$().workflow.workflow.promptWorkflow.userInputs,
            postDescription: this.workflowForm.value.postDescription,
          },
          aiResults: {
            postDescription: "",
            postGoal: "",
            postFinal: "",
            fileSummary:
              this.stateSignal$().workflow.workflow.promptWorkflow.aiResults
                .fileSummary,
          },
          uploadedFiles: [
            ...this.stateSignal$().workflow.workflow.promptWorkflow
              .uploadedFiles,
          ],
        },
      },
      this.stateSignal$().workflow.workflow.uid,
    );
  }

  updateWorkflow(): void {
    this.stateService.updateWorkflow({
      ...this.stateSignal$().workflow.workflow,
      promptWorkflow: {
        ...this.stateSignal$().workflow.workflow.promptWorkflow,
        userInputs: {
          ...this.stateSignal$().workflow.workflow.promptWorkflow.userInputs,
          postDescription: this.workflowForm.value.postDescription,
        },
      },
    });
  }

  onFileChange(fileList: any | Event) {
    const workflow: Workflow = {
      ...this.stateSignal$().workflow.workflow,
      promptWorkflow: {
        ...this.stateSignal$().workflow.workflow.promptWorkflow,
        aiResults: {
          ...this.stateSignal$().workflow.workflow.promptWorkflow.aiResults,
          fileSummary: "",
        },
      },
    };
    if (
      this.stateSignal$().workflow.workflow.promptWorkflow.uploadedFiles?.length > 0
    ) {
      this.panelOpenState = false;
      this.stateService.deleteFile(
        workflow.promptWorkflow.uploadedFiles[0],
        workflow,
      );
    }
    Object.keys(fileList).map((key) => {
      this.stateService.uploadFile(fileList[key], workflow);
    });
  }

  resetChangeDetection(): void {
    let input = document.getElementById("file");
    input["value"] = null;
  }

  deleteFile(file) {
    this.panelOpenState = false;
    this.stateService.deleteFile(file, this.stateSignal$().workflow.workflow);
  }

  ngOnDestroy(): void {
    if (!this.charCount?.closed) {
      this.charCount.unsubscribe();
    }
  }
}
